import {
  startDrawing as startDrawingUtil,
  continueDrawing as continueDrawingUtil,
  finishDrawing as finishDrawingUtil,
  finishPolygon as finishPolygonUtil,
  cancelDrawing as cancelDrawingUtil,
  confirmDrawing as confirmDrawingUtil,
} from "../utils/drawingUtils";
import { findWordsInShape } from "../utils/geometryCalculations";
import { useCallback, useEffect } from "react";

/**
 * Hook for drawing operations - consolidates all drawing-related callbacks
 * Provides clean interface for shape drawing functionality
 */
const useDrawingOperations = (
  drawingState,
  modalState,
  tableState,
  pdfState,
  refs,
  getCanvasCoordinates,
  convertCanvasToPdfCoordinates
) => {
  const {
    drawingMode,
    setDrawingMode,
    isDrawing,
    setIsDrawing,
    drawingShape,
    setDrawingShape,
    drawingStartPoint,
    setDrawingStartPoint,
    polygonPoints,
    setPolygonPoints,
    previewWords,
    setPreviewWords,
    pendingShapeWords,
    setPendingShapeWords,
  } = drawingState;

  const { setShowDrawingConfirmation, showDrawingConfirmation } = modalState;
  const {
    selectedTable,
    tableData,
    setTableData,
    setSelectedTable,
  } = tableState;
  const { currentPage } = pdfState;
  const { canvasRef, highlightContainerRef, pdfPageDimensions } = refs;

  // Update preview words for polygon as it's being drawn
  useEffect(() => {
    if (
      drawingMode === "polygon" &&
      polygonPoints.length >= 3 &&
      !showDrawingConfirmation
    ) {
      const polygonShape = {
        type: "polygon",
        points: polygonPoints,
      };
      const wordsInShape = findWordsInShape(
        polygonShape,
        selectedTable,
        currentPage,
        pdfPageDimensions
      );
      setPreviewWords(wordsInShape);
    }
  }, [
    drawingMode,
    polygonPoints,
    showDrawingConfirmation,
    selectedTable,
    currentPage,
    pdfPageDimensions,
    setPreviewWords,
  ]);

  // Start drawing operation
  const startDrawing = useCallback(
    (event) => {
      startDrawingUtil(event, {
        drawingMode,
        isResizing: false, // Drawing operations are separate from resizing
        isDraggingGridLine: false, // Drawing operations are separate from grid lines
        getCanvasCoordinates,
        convertCanvasToPdfCoordinates,
        canvasRef,
        highlightContainerRef,
        pdfPageDimensions,
        setIsDrawing,
        setDrawingStartPoint,
        setPolygonPoints,
        setDrawingShape,
      });
    },
    [
      drawingMode,
      getCanvasCoordinates,
      convertCanvasToPdfCoordinates,
      canvasRef,
      highlightContainerRef,
      pdfPageDimensions,
      setIsDrawing,
      setDrawingStartPoint,
      setPolygonPoints,
      setDrawingShape,
    ]
  );

  // Continue drawing operation
  const continueDrawing = useCallback(
    (event) => {
      continueDrawingUtil(event, {
        isDrawing,
        drawingStartPoint,
        drawingMode,
        isDraggingGridLine: false, // Drawing operations are separate from grid line drag
        getCanvasCoordinates,
        convertCanvasToPdfCoordinates,
        canvasRef,
        highlightContainerRef,
        pdfPageDimensions,
        setDrawingShape,
      });
    },
    [
      drawingMode,
      isDrawing,
      drawingStartPoint,
      getCanvasCoordinates,
      convertCanvasToPdfCoordinates,
      canvasRef,
      highlightContainerRef,
      pdfPageDimensions,
      setDrawingShape,
    ]
  );

  // Finish drawing operation
  const finishDrawing = useCallback(() => {
    finishDrawingUtil({
      isDrawing,
      drawingMode,
      drawingShape,
      findWordsInShape,
      selectedTable,
      currentPage,
      pdfPageDimensions,
      setPendingShapeWords,
      setShowDrawingConfirmation,
      setIsDrawing,
      setDrawingStartPoint,
      setDrawingShape,
    });
  }, [
    isDrawing,
    drawingMode,
    drawingShape,
    selectedTable,
    currentPage,
    pdfPageDimensions,
    setPendingShapeWords,
    setShowDrawingConfirmation,
    setIsDrawing,
    setDrawingStartPoint,
    setDrawingShape,
  ]);

  // Finish polygon drawing
  const finishPolygon = useCallback(() => {
    finishPolygonUtil({
      drawingMode,
      polygonPoints,
      findWordsInShape,
      selectedTable,
      currentPage,
      pdfPageDimensions,
      setPendingShapeWords,
      setShowDrawingConfirmation,
      setPolygonPoints,
      setIsDrawing,
    });
  }, [
    drawingMode,
    polygonPoints,
    selectedTable,
    currentPage,
    pdfPageDimensions,
    setPendingShapeWords,
    setShowDrawingConfirmation,
    setPolygonPoints,
    setIsDrawing,
  ]);

  // Cancel drawing operation
  const cancelDrawing = useCallback(() => {
    cancelDrawingUtil({
      setIsDrawing,
      setDrawingStartPoint,
      setDrawingShape,
      setPolygonPoints,
      setDrawingMode,
      setPreviewWords,
    });
  }, [
    setIsDrawing,
    setDrawingStartPoint,
    setDrawingShape,
    setPolygonPoints,
    setDrawingMode,
    setPreviewWords,
  ]);

  // Confirm drawing and update table
  const confirmDrawing = useCallback(() => {
    confirmDrawingUtil({
      pendingShapeWords,
      selectedTable,
      tableData,
      setTableData,
      setSelectedTable,
      setShowDrawingConfirmation,
      setPendingShapeWords,
      setDrawingMode,
    });
  }, [
    pendingShapeWords,
    selectedTable,
    tableData,
    setTableData,
    setSelectedTable,
    setShowDrawingConfirmation,
    setPendingShapeWords,
    setDrawingMode,
  ]);

  // Get drawing status information
  const getDrawingStatus = useCallback(() => {
    return {
      isActive: !!drawingMode,
      mode: drawingMode,
      isDrawing,
      hasShape: !!drawingShape,
      polygonPointCount: polygonPoints?.length || 0,
      previewWordCount: previewWords?.length || 0,
    };
  }, [drawingMode, isDrawing, drawingShape, polygonPoints, previewWords]);

  // Reset all drawing state
  const resetDrawingState = useCallback(() => {
    setDrawingMode(null);
    setIsDrawing(false);
    setDrawingShape(null);
    setDrawingStartPoint(null);
    setPolygonPoints([]);
    setPreviewWords([]);
    setPendingShapeWords([]);
  }, [
    setDrawingMode,
    setIsDrawing,
    setDrawingShape,
    setDrawingStartPoint,
    setPolygonPoints,
    setPreviewWords,
    setPendingShapeWords,
  ]);

  return {
    // Drawing operations
    startDrawing,
    continueDrawing,
    finishDrawing,
    finishPolygon,
    cancelDrawing,
    confirmDrawing,

    // Utility operations
    resetDrawingState,
    getDrawingStatus,

    // State access
    drawingMode,
    isDrawing,
    drawingShape,
    previewWords,
    pendingShapeWords,
  };
};

export default useDrawingOperations;
